import { GuideButton, useGuideValue } from "../guides/GuideProvider";
import Collapse from "../components/Collapse";
import { useState, useEffect, useMemo } from "react";
import axios from 'axios';
import { useLocation, useNavigate } from "react-router-dom";
import { filterVideoCatalog, normalizeVideoSearchText } from "../utils/videoLibrary";

const API_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_LOCAL_API_URL

function isDirectPathChild(path, parentPath) {
  if (parentPath === "") {
    return path.startsWith("/");
  }

  return path === parentPath || path.startsWith(`${parentPath}/`);
}

function getSubfolders(paths, targetPath, depth) {
  const segmentIndex = depth + 1;

  if (targetPath === "") {
    return Array.from(
      new Set(paths.map((obj) => obj.split("/")[1]).filter(Boolean))
    );
  }

  return Array.from(
    new Set(
      paths
        .filter((obj) => isDirectPathChild(obj, targetPath))
        .map((obj) => obj.split("/")[segmentIndex])
        .filter(Boolean)
    )
  );
}

function isLeafFolder(paths, targetPath, depth) {
  if (!paths.includes(targetPath)) {
    return false;
  }

  return getSubfolders(paths, targetPath, depth).length === 0;
}

function getFolderPath(parentPath, folder) {
  return `${parentPath}/${folder}`;
}

function getFolderDisplayName(folderMetadata, folderPath, fallbackName) {
  return folderMetadata[folderPath]?.displayName || fallbackName;
}

function FileBrowser({ exercise, setExercise }) {
  const { state, search } = useLocation();
  const linkedClip = new URLSearchParams(search).get('clip') || '';
  const [selectedClip, setSelectedClip] = useState(linkedClip);
  useEffect(() => {
    setSelectedClip(linkedClip);
    if (linkedClip) { setSearchQuery(''); setShowFavorites(false); }
  }, [linkedClip]);
  const [bucketPaths, setBucketPaths] = useState([]);
  const [videoMetadata, setVideoMetadata] = useState({});
  const [folderMetadata, setFolderMetadata] = useState({});
  const [videoCatalog, setVideoCatalog] = useState([]);
  const [favoriteVideos, setFavoriteVideos] = useState(() => new Set());
  const [favoriteBusy, setFavoriteBusy] = useState(() => new Set());
  const [searchQuery, setSearchQuery] = useState("");
  useGuideValue("search", Boolean(searchQuery.trim()));
  const [showFavorites, setShowFavorites] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState("");
  const [favoriteError, setFavoriteError] = useState("");
  const [currentPath, setCurrentPath] = useState({
    path: "",
    subfolders: [],
    depth: 0,
    videos: []
  });
  const navigate = useNavigate();

  async function getFilepaths() {
    try {
      await axios.get(`${API_URL}/structure/structure`, {
          headers: {
          'Content-Type': 'application/json',
          'token': localStorage.getItem("token")
          }
      }).then((response) => {
        setBucketPaths(bucketPaths => response.data);
      });
        
    } catch (error) {
      console.error('Error fetching filepaths:', error);
    }
  };

  async function getVideoMetadata() {
    try {
      const response = await axios.get(`${API_URL}/video-metadata/all`, {
        headers: {
          "Content-Type": "application/json",
          token: localStorage.getItem("token")
        }
      });
      setVideoMetadata(response.data || {});
    } catch (error) {
      console.error("Error fetching video metadata:", error);
    }
  }

  async function getFolderMetadata() {
    try {
      const response = await axios.get(`${API_URL}/folder-metadata/all`, {
        headers: {
          "Content-Type": "application/json",
          token: localStorage.getItem("token")
        }
      });
      setFolderMetadata(response.data || {});
    } catch (error) {
      console.error("Error fetching folder metadata:", error);
    }
  }

  async function getVideoCatalog() {
    try {
      setCatalogLoading(true);
      const response = await axios.get(`${API_URL}/bucket/videos`, {
        headers: {
          "Content-Type": "application/json",
          token: localStorage.getItem("token")
        }
      });
      setVideoCatalog(response.data || []);
      setCatalogError("");
    } catch (error) {
      setCatalogError(error.response?.data?.message || "Kunde inte hämta videokatalogen.");
    } finally {
      setCatalogLoading(false);
    }
  }

  async function getFavoriteVideos() {
    try {
      const response = await axios.get(`${API_URL}/users/me/favorite-videos`, {
        headers: { token: localStorage.getItem("token") }
      });
      setFavoriteVideos(new Set(response.data.favoriteVideos || []));
      setFavoriteError("");
    } catch (error) {
      setFavoriteError(error.response?.data?.message || "Kunde inte hämta dina favoriter.");
    }
  }
  

  function bucketRequest(queryPath) {
    return new Promise((resolve, reject) => {
      var videos = [];
      axios.get(`${API_URL}/bucket/list-folder`, {
        headers: {
          'Content-Type': 'application/json',
          'token': localStorage.getItem("token")
        },
        params: {
          path: encodeURIComponent(queryPath)
        }
      })
        .then(response => {
          videos = response.data;
          resolve(videos);
        })
        .catch(err => {
          // TODO Handle the error, maybe give some feedback to the user
          console.log(err);
          reject(err);
        });
    });
  };

  async function handleClickForward(folder) {
    // Update current folder
    let newPath = `${currentPath.path}/${folder}`;
    let depth = currentPath.depth + 1;
    let videos = [];
    let pathEnd = isLeafFolder(bucketPaths, newPath, depth);
    if (newPath !== "" && pathEnd) {
      let queryPath = newPath.slice(1);
      // Fetch the array from the API endpoint
      videos = await bucketRequest(queryPath);
      // Confirm file format
      videos = videos.filter(video => video.includes(".mp4"));
    }
    // Get subfolders for the new path. Limit to account for the depth by checking if we are already at a complete path
    let subfolders = !pathEnd ? getSubfolders(bucketPaths, newPath, depth) : [];
    setCurrentPath({
      path: newPath,
      subfolders,
      depth,
      videos
    });
  }

  function handleClickBack() {
    // Prevent out of bound by returning early if at root
    if (currentPath.path === "") return
    let newPath = currentPath.path.split('/').slice(0, -1).join('/');
    let depth = currentPath.depth - 1;
    let subfolders = getSubfolders(bucketPaths, newPath, depth);

    setCurrentPath({
      path: newPath,
      subfolders,
      depth,
      videos: []
    });
  }

  async function navigateToPath(targetPath) {
    if (targetPath === currentPath.path) return;

    if (targetPath === "") {
      const subfolders = getSubfolders(bucketPaths, "", 0);

      setCurrentPath({
        path: "",
        subfolders,
        depth: 0,
        videos: []
      });
      return;
    }

    const depth = targetPath.split("/").filter(Boolean).length;
    const leafFolder = isLeafFolder(bucketPaths, targetPath, depth);
    let videos = [];

    if (leafFolder) {
      const queryPath = targetPath.slice(1);
      videos = await bucketRequest(queryPath);
      videos = videos.filter(video => video.includes(".mp4"));
    }

    const subfolders = !leafFolder
      ? getSubfolders(bucketPaths, targetPath, depth)
      : [];

    setCurrentPath({
      path: targetPath,
      subfolders,
      depth,
      videos
    });
  }

  useEffect(() => {
    getFilepaths();
    getVideoMetadata();
    getFolderMetadata();
    getVideoCatalog();
    getFavoriteVideos();
  }, []);

  useEffect(() => {
    if (bucketPaths.length === 0 || currentPath.path !== "") {
      return;
    }

    setCurrentPath((currentPath) => ({
      ...currentPath,
      subfolders: getSubfolders(bucketPaths, "", 0)
    }));
  }, [bucketPaths, currentPath.path]);

  const breadcrumbParts = currentPath.path
    ? currentPath.path.split("/").filter(Boolean)
    : [];

  const normalizedQuery = normalizeVideoSearchText(searchQuery);
  const filteredCatalog = useMemo(() => filterVideoCatalog(videoCatalog, {
    clipPath: selectedClip,
    query: normalizedQuery,
    favorites: favoriteVideos,
    favoritesOnly: showFavorites
  }), [favoriteVideos, normalizedQuery, showFavorites, videoCatalog, selectedClip]);

  const isCatalogView = Boolean(normalizedQuery) || showFavorites || Boolean(selectedClip);

  async function toggleFavorite(clipPath) {
    if (favoriteBusy.has(clipPath)) {
      return;
    }
    const wasFavorite = favoriteVideos.has(clipPath);
    const previousFavorites = new Set(favoriteVideos);
    const optimisticFavorites = new Set(favoriteVideos);
    if (wasFavorite) optimisticFavorites.delete(clipPath);
    else optimisticFavorites.add(clipPath);
    setFavoriteVideos(optimisticFavorites);
    setFavoriteBusy((current) => new Set(current).add(clipPath));
    setFavoriteError("");

    try {
      const response = await axios.patch(`${API_URL}/users/me/favorite-videos`, {
        clipPath,
        favorite: !wasFavorite
      }, {
        headers: {
          "Content-Type": "application/json",
          token: localStorage.getItem("token")
        }
      });
      setFavoriteVideos(new Set(response.data.favoriteVideos || []));
    } catch (error) {
      setFavoriteVideos(previousFavorites);
      setFavoriteError(error.response?.data?.message || "Kunde inte uppdatera favoriten.");
    } finally {
      setFavoriteBusy((current) => {
        const next = new Set(current);
        next.delete(clipPath);
        return next;
      });
    }
  }

  function findCatalogVideo(folderPath, fileName) {
    const filePath = `${folderPath}/${fileName}`.replace(/\/{2,}/g, "/");
    return videoCatalog.find((video) => video.filePath === filePath);
  }

  return (
    <>
      <div className="flex flex-col items-center px-2 sm:px-3">
        <div className="max-w-2xl w-full py-2">
          <div className="relative rounded-b-[22px] border-b border-slate-200 bg-white/92 px-2 py-3 shadow-sm backdrop-blur-md sm:sticky sm:top-0 sm:z-10 sm:rounded-b-[28px] sm:px-3">
            <div className="px-1">
              <div className="text-sm font-semibold text-slate-800">
                Videobibliotek
              </div>
              <div data-guide="video-navigation" className="mt-2 flex flex-wrap items-center gap-y-2 text-sm text-slate-500">
                <button
                  className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 font-medium text-sky-700 transition-colors hover:bg-sky-100 hover:text-sky-900"
                  onClick={() => navigateToPath("")}
                >
                  Start
                </button>
                {breadcrumbParts.map((part, index) => {
                  const targetPath = `/${breadcrumbParts.slice(0, index + 1).join("/")}`;
                  const isCurrent = index === breadcrumbParts.length - 1;
                  const displayName = getFolderDisplayName(folderMetadata, targetPath, part);

                  return (
                    <span key={targetPath}>
                      <span className="mx-2 inline-flex items-center text-slate-400" aria-hidden="true">
                        <svg
                          className="h-3.5 w-3.5"
                          viewBox="0 0 16 16"
                          fill="currentColor"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M6 3.5L10.5 8L6 12.5L7.4 13.9L13.3 8L7.4 2.1L6 3.5Z"
                          />
                        </svg>
                      </span>
                      {isCurrent ? (
                        <span className="rounded-full border border-slate-200 bg-slate-100 px-3 py-1 font-medium text-slate-700">
                          {displayName}
                        </span>
                      ) : (
                        <button
                          className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 font-medium text-sky-700 transition-colors hover:bg-sky-100 hover:text-sky-900"
                          onClick={() => navigateToPath(targetPath)}
                        >
                          {displayName}
                        </button>
                      )}
                    </span>
                  );
                })}
              </div>
            </div>
            <GuideButton id="videos" />
            <div className="mt-3">
              <label className="sr-only" htmlFor="video-search">Sök i videobiblioteket</label>
              <div className="flex gap-2">
                <input
                  data-guide="video-search"
                  id="video-search"
                  type="search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  className="theme-input min-w-0 flex-1 px-4 py-3 text-base sm:text-sm"
                  placeholder="Sök på video, beskrivning eller mapp"
                  autoComplete="off"
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="inline-flex min-h-[44px] items-center rounded-2xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                    onClick={() => setSearchQuery("")}
                  >
                    Rensa
                  </button>
                )}
              </div>
              <button
                type="button"
                className={`mt-2 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-2xl border px-4 py-2 text-sm font-semibold transition-colors sm:w-auto ${showFavorites ? "border-amber-300 bg-amber-50 text-amber-700" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}
                data-guide="video-favorites"
                onClick={() => setShowFavorites((value) => !value)}
                aria-pressed={showFavorites}
              >
                <span aria-hidden="true">★</span>
                {showFavorites ? "Visar favoriter" : `Favoriter (${favoriteVideos.size})`}
              </button>
              {isCatalogView && !catalogLoading && (
                <p className="mt-2 text-xs text-slate-500" aria-live="polite">
                  {filteredCatalog.length} {filteredCatalog.length === 1 ? "video" : "videor"} hittades
                </p>
              )}
            </div>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <button
                className="flex-1 rounded-[20px] border border-red-200 bg-gradient-to-r from-red-500 to-rose-500 px-5 py-3 font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                onClick={() => navigate(-1)}
              >
                Tillbaka
              </button>
              {currentPath.depth !== 0 && (
                <button
                  className="flex-1 rounded-[20px] border border-sky-200/80 bg-gradient-to-r from-sky-700 to-cyan-500 px-5 py-3 font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                  onClick={handleClickBack}
                >
                  Föregående mapp
                </button>
              )}
            </div>
          </div>
          <div className="pt-3">
          {selectedClip && <div className="theme-panel mb-3 flex items-center justify-between gap-3">
            <span>Övning från AI-assistenten</span>
            <button className="theme-button-secondary px-3 py-2" onClick={() => {
              setSelectedClip('');
              navigate('/videos', { replace: true, state });
            }}>Visa hela biblioteket</button>
          </div>}
          {favoriteError && <div className="theme-alert-error mb-3" role="alert">{favoriteError}</div>}
          {catalogError && isCatalogView && <div className="theme-alert-error mb-3" role="alert">{catalogError}</div>}
          {catalogLoading && isCatalogView && <div className="theme-empty-state">Laddar videokatalogen...</div>}
          {!catalogLoading && isCatalogView && filteredCatalog.length === 0 && (
            <div className="theme-empty-state">
              {showFavorites && !normalizedQuery
                ? "Du har inga favoritmarkerade videor ännu."
                : "Inga videor matchar sökningen."}
            </div>
          )}
          {!catalogLoading && isCatalogView && filteredCatalog.map((video) => (
            <Collapse
              key={`${video.filePath}:${selectedClip}`}
              initiallyOpen={Boolean(selectedClip)}
              title={video.fileName}
              path={video.folderPath}
              exercise={exercise}
              setExercise={setExercise}
              state={state}
              videoMetadata={videoMetadata}
              displayTitle={video.title}
              description={video.description}
              folderLabel={video.folderPath.split("/").filter(Boolean).join(" › ")}
              isFavorite={favoriteVideos.has(video.clipPath)}
              favoriteDisabled={favoriteBusy.has(video.clipPath)}
              onToggleFavorite={() => toggleFavorite(video.clipPath)}
            />
          ))}
          {!isCatalogView && currentPath.subfolders.length !== 0 && currentPath.subfolders.map(folder => {
            const folderPath = getFolderPath(currentPath.path, folder);
            const displayName = getFolderDisplayName(folderMetadata, folderPath, folder);

            return <button type="button" key={folder} className="my-3 flex min-h-[56px] w-full cursor-pointer items-center justify-between rounded-[20px] border border-sky-200/80 bg-gradient-to-r from-white via-sky-50 to-cyan-50 px-4 py-3 text-left text-slate-800 shadow-sm transition-all hover:-translate-y-0.5 hover:border-sky-300 hover:shadow-md sm:rounded-[24px] sm:px-5 sm:py-4" onClick={() => handleClickForward(folder)}><span className="font-semibold">{displayName}</span><span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/80 text-sky-700 shadow-sm ring-1 ring-sky-100"><svg className="h-4 w-4" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M6 3.5L10.5 8L6 12.5L7.4 13.9L13.3 8L7.4 2.1L6 3.5Z" /></svg></span></button>;
          })}
          {!isCatalogView && currentPath.videos.length !== 0 && currentPath.videos.map(video => {
            const catalogVideo = findCatalogVideo(currentPath.path, video);
            const clipPath = catalogVideo?.clipPath || `${currentPath.path}/${video}`.replace(/\.[^/.]+$/, "").replace(/_trim$/i, "");
            return <Collapse key={video} title={video} path={currentPath.path} exercise={exercise} setExercise={setExercise} state={state} videoMetadata={videoMetadata} isFavorite={favoriteVideos.has(clipPath)} favoriteDisabled={favoriteBusy.has(clipPath)} onToggleFavorite={() => toggleFavorite(clipPath)} />
          })
          }
          </div>
        </div>
      </div>
    </>
  );
}

export default FileBrowser;
